/* Built In Imports */
/* External Imports */
import { Box, Flex } from '@chakra-ui/react';
import Slider from 'react-slick';

/* Internal Imports */
/* Components */
import SimpleButton from '@components/Buttons/SimpleButton';
import StructuredTextContent from '@components/UI/StructuredTextContent';
import config from '@config';

/* Services */
/* Styles */
import { useState } from 'react';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';

/**
 * Renders CSRSharingTopSliderGroup Component
 *
 * @param {object} sectionContent - Data from api
 * @param {string} region - Region of page
 * @param {string} lang - Language of page
 * @returns
 */

const CSRSharingTopSliderGroup = ({ sectionContent, region, language }) => {
  const [textIndex, setTextIndex] = useState(0);
  const styles = {
    default: 'polygon(0 0, 100% 0, 100% 100%, 0 85%)',
    'without-bottom-angle': '',
    '': 'polygon(0 0, 100% 0, 100% 100%, 0 85%)',
  };

  const NextArrow = props => {
    const { onClick } = props;
    return (
      <Box
        pos="absolute"
        textAlign={'center'}
        top={'45%'}
        zIndex="15"
        cursor="pointer"
        bgColor="#fff"
        border="0"
        borderRadius="100%"
        w="40px"
        h="40px"
        bgPos="center"
        bgRepeat="no-repeat"
        color="transparent"
        bgImage={`url(${config.staticPath}/assets/images/right-arrow.svg)`}
        right={{ base: '2%', md: '3%' }}
        bgSize="10px"
        onClick={onClick}
        boxShadow="0 0 15px 0 rgb(0 0 0 / 30%)"
      ></Box>
    );
  };

  const PrevArrow = props => {
    const { onClick } = props;
    return (
      <Box
        pos="absolute"
        top={'45%'}
        zIndex="15"
        cursor="pointer"
        bgSize="10px"
        bgColor="#fff"
        border="0"
        borderRadius="100%"
        w="40px"
        h="40px"
        bgPos="center"
        bgRepeat="no-repeat"
        color="transparent"
        bgImage={`url(${config.staticPath}/assets/images/left-arrow.svg)`}
        left={{ base: '10px', md: '20px' }}
        onClick={onClick}
        boxShadow="0 0 15px 0 rgb(0 0 0 / 30%)"
      />
    );
  };
  const heightValue = language === 'ta' ? 'none' : '50px';

  const settings = {
    dots: false,
    speed: 500,
    autoplay: true,
    autoplaySpeed: 8000,
    slidesToShow: 1,
    slidesToScroll: 1,
    centerMode: false,
    infinite: true,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    responsive: [
      {
        breakpoint: 834,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          initialSlide: 1,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
    beforeChange: (_, next) => setTextIndex(next),
    customPaging: () => (
      <Text
        cursor="pointer"
        display="inline-block"
        w="10px"
        h="10px"
        m="0 8px"
        bgColor="#cf4520"
        borderRadius="50%"
      />
    ),
  };

  return (
    <Box pos="relative" height="auto" h={{ base: 'auto', lg: '500px' }}>
      <Slider {...settings}>
        {sectionContent?.slides?.length > 0 &&
          sectionContent.slides.map((slide, index) => (
            <Box key={index} w="100%" pos="relative">
              <Box
                className="bannerimge"
                bgColor={{ base: '#f8f5f0;', sm: 'transparent' }}
                h={{ base: '105.9375vw', lg: '500px' }}
                minH={{ base: '105.9375vw', lg: '500px' }}
                background="radial-gradient(circle at 0% 0.5%, rgb(228 222 212) 0.1%, rgb(224, 226, 228) 100.2%)"
                backgroundRepeat="no-repeat"
                backgroundSize="cover"
                backgroundPosition={{
                  base: '45%',
                  sm: '45%',
                  lg: 'top center',
                  xl: '0',
                }}
                backgroundImage={{
                  lg: slide.desktopImage?.url,
                  md: slide.mobileImage?.url,
                  base: slide.mobileImage?.url || slide.desktopImage?.url,
                }}
                mb="0"
                clipPath={styles[slide.style]}
              />
            </Box>
          ))}
      </Slider>

      {/* MOBILE TEXT CONTENT */}
      <Flex
        display={{ base: 'flex', lg: 'none' }}
        flexDirection="column"
        mt="-10px"
        p={['15px', '24px', 4, 14]}
        fontSize={{ sm: '44px', base: '30px' }}
        color="#28231e"
        background="#FFFCF5"
        lineHeight="1.11"
        fontFamily="FedraSansStd-A-medium"
      >
        <Box as="h1">{sectionContent?.slides[textIndex]?.title}</Box>

        {sectionContent?.slides[textIndex]?.description && (
          <Box
            fontSize={['16px', '18px']}
            fontFamily="FedraSansStd-book"
            lineHeight="1.44"
            textAlign="left"
            color="#b95c23"
            pt="32px"
          >
            {StructuredTextContent(
              sectionContent.slides[textIndex].description
            )}
          </Box>
        )}

        {sectionContent?.slides[textIndex]?.cta && (
          <Box
            display="grid"
            placeItems="baseline"
            borderRadius="3px"
            width={{ sm: '200px' }}
            h="50px"
            mt="30px"
          >
            <SimpleButton
              style={sectionContent.slides[textIndex].cta}
              region={region}
              lang={language}
            />
          </Box>
        )}
      </Flex>

      {/* DESKTOP TEXT CONTENT */}
      <Box
        pos="absolute"
        top={{ base: '500px', md: '0' }}
        left="0"
        bottom="0"
        right={{ base: '0', md: '40%' }}
        display={{ base: 'none', lg: 'block' }}
        zIndex={10}
        background={{
          base: 'none',
          md: 'linear-gradient(95.19deg, #FFFFFF 15.12%, #ffffff 72.03%, rgba(255, 252, 245, 0) 93.98%)',
        }}
      >
        <Flex
          flexDirection="column"
          justifyContent="center"
          h="100%"
          mt="0"
          p={['15px', '24px', 4, 14]}
          pr="0 !important"
          pl="66px !important"
          fontSize={{ sm: '44px', base: '30px' }}
          color={
            sectionContent.style === 'dark-background'
              ? 'white'
              : sectionContent.style === 'red-title-text'
              ? '#E1434D'
              : '#28231e'
          }
          lineHeight="1.11"
          fontFamily="FedraSansStd-A-medium"
          maxW={{ base: '420px', xl: '526px' }}
        >
          <Box as="h1" pt={['0', '40px']} fontSize={[0, 32, 44]}>
            {sectionContent.slides[textIndex]?.title}
          </Box>

          {sectionContent.slides[textIndex]?.description && (
            <Box
              fontSize={['16px', '18px']}
              fontFamily="FedraSansStd-book"
              lineHeight="1.44"
              textAlign="left"
              color={
                sectionContent.style === 'dark-background'
                  ? 'white'
                  : sectionContent.style === 'red-title-text'
                  ? '#E1434D'
                  : '#b95c23'
              }
              pt={['20px', '32px']}
            >
              {StructuredTextContent(
                sectionContent.slides[textIndex].description
              )}
            </Box>
          )}

          {sectionContent.slides[textIndex]?.cta && (
            <Box width="220px" h={heightValue} mt="20px">
              <SimpleButton
                style={sectionContent.slides[textIndex].cta}
                region={region}
                lang={language}
              />
            </Box>
          )}
        </Flex>
      </Box>
      <style>
        {`
        @media (min-width: 960px) and (max-width: 1924px) {
          .bannerimge {
            background-position: 100%;
          }
        }
      `}
      </style>
    </Box>
  );
};

export default CSRSharingTopSliderGroup;
